import { useCallback, useState } from "react";

import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import { useLazyGetPendingBySenderQuery } from "@/pages/Private/redux/schedule/schedule.api";
import { useDeleteEmailMutation } from "@/pages/Private/redux/email/email.api";
import { useDeleteUnipileProfileMutation } from "@/pages/Private/redux/unipile/unipile.api";

import { CampaignAction, CampaignPending } from "../schema/pendingSchedules";

interface UseSenderDeletionOptions {
	senderType: "email" | "linkedin" | "user";
	emailId?: number;
	onSuccess?: () => void;
	onError?: (error: unknown) => void;
}

interface UseSenderDeletionReturn {
	initiateDeletion: () => Promise<void>;
	confirmDeletion: (campaignActions: CampaignAction[]) => Promise<void>;
	cancelDeletion: () => void;
	showPendingDialog: boolean;
	totalCount: number;
	campaigns: CampaignPending[];
	isCheckingPending: boolean;
	isDeleting: boolean;
}

export const useSenderDeletion = ({
	senderType,
	emailId,
	onSuccess,
	onError,
}: UseSenderDeletionOptions): UseSenderDeletionReturn => {
	const { user } = useAppSelector(authSelector);

	const [showPendingDialog, setShowPendingDialog] = useState(false);
	const [totalCount, setTotalCount] = useState(0);
	const [campaigns, setCampaigns] = useState<CampaignPending[]>([]);

	const [getPendingBySender, { isFetching: isCheckingPending }] = useLazyGetPendingBySenderQuery();
	const [deleteEmail, { isLoading: isDeletingEmail }] = useDeleteEmailMutation();
	const [deleteUnipileProfile, { isLoading: isDeletingUnipile }] =
		useDeleteUnipileProfileMutation();

	const isDeleting = isDeletingEmail || isDeletingUnipile;

	const performDeletion = useCallback(
		async (campaignActions?: CampaignAction[]) => {
			try {
				if (senderType === "email" && emailId) {
					await deleteEmail({ id: emailId, campaignActions }).unwrap();
				} else {
					await deleteUnipileProfile({
						type: senderType as "email" | "linkedin",
						campaignActions,
					}).unwrap();
				}

				onSuccess?.();
			} catch (error) {
				onError?.(error);
			}
		},
		[senderType, emailId, deleteEmail, deleteUnipileProfile, onSuccess, onError]
	);

	const initiateDeletion = useCallback(async () => {
		if (!user?.id) {
			return;
		}

		try {
			const result = await getPendingBySender({
				userId: user.id,
				...(senderType === "email" && emailId ? { emailId } : {}),
			}).unwrap();

			if (result.totalCount > 0) {
				setTotalCount(result.totalCount);
				setCampaigns(result.campaigns);
				setShowPendingDialog(true);
			} else {
				await performDeletion();
			}
		} catch (error) {
			onError?.(error);
		}
	}, [user?.id, senderType, emailId, getPendingBySender, performDeletion, onError]);

	const confirmDeletion = useCallback(
		async (campaignActions: CampaignAction[]) => {
			await performDeletion(campaignActions);
			setShowPendingDialog(false);
			setCampaigns([]);
			setTotalCount(0);
		},
		[performDeletion]
	);

	const cancelDeletion = useCallback(() => {
		setShowPendingDialog(false);
		setCampaigns([]);
		setTotalCount(0);
	}, []);

	return {
		initiateDeletion,
		confirmDeletion,
		cancelDeletion,
		showPendingDialog,
		totalCount,
		campaigns,
		isCheckingPending,
		isDeleting,
	};
};
